// User-defined attributes: the LOCK state an attribute's own rules imply when
// a record or line opens.
//
// A rule-mode item change with `protect` entries fires only on a live change,
// so opening a form computes the initial lock state by firing each such rule
// once over the stored values (TxnScreen `seedProtect`, `collectSeededProtect`).
// The attribute columns arrive later — with the `uda_resolve` answer — so the
// screen seeds THEIR rules again once it has them, through the same helper and
// guarded by the same kind of sequence token (a stale answer is dropped).

import type { ColumnDef, ItemChangeResult, SqlModel } from '../txn/types'
import { collectSeededProtect } from '../txn/list/useItemChange'
import { isUdaColumn } from './merge'
import type { UdaColumnDef } from './types'

/** A stand-in form holding only the attribute columns (no deciding values) —
 *  what the seed walks, so the form's own columns are not fired twice. */
export function udaOnlyModel(columns: readonly UdaColumnDef[], attrKeys?: readonly string[] | null): SqlModel {
  const keep = attrKeys ? new Set(attrKeys) : null
  return {
    columns: columns
      .filter(c => isUdaColumn(c) && !c.uda.driver && (!keep || keep.has(String(c.uda.key))))
      .map(c => ({ column: c as ColumnDef })),
  } as unknown as SqlModel
}

/** Fire the protect rules of the given attribute columns once over `values`
 *  and return the lock map they imply ({form_key: 1 lock | 0 unlock}). */
export function seedUdaProtect(
  columns: readonly UdaColumnDef[], values: Record<string, unknown>,
  fire: (col: ColumnDef, header: Record<string, unknown>) => Promise<ItemChangeResult | null>,
  attrKeys?: readonly string[] | null,
): Promise<Record<string, 0 | 1>> {
  if (!columns.length) return Promise.resolve({})
  return collectSeededProtect(udaOnlyModel(columns, attrKeys), values, fire)
}

/** The part of an item change's `protect` map that addresses attributes —
 *  what a line keeps as its attribute locks (a line's own columns have no
 *  per-row lock state of their own). */
export function udaProtectOf(protect: Record<string, unknown> | null | undefined): Record<string, 0 | 1> {
  const out: Record<string, 0 | 1> = {}
  for (const [k, v] of Object.entries(protect || {})) {
    if (!k.toLowerCase().startsWith('uda_')) continue
    out[k] = v === 1 || v === true || v === '1' ? 1 : 0
  }
  return out
}
